import { BACKEND_API_URL } from "@/shared/config/app";
import { AVATAR_PAYMENT_API_URL } from "@/shared/config/stripe";
import { referralBody } from "@/shared/lib/referral";
import { getAttributionPayload } from "@/shared/lib/analytics";
import { trackTokenPackCheckout } from "./tokenPackAnalytics";

export interface QuestionsCheckoutReturn {
    /** Страница, куда Stripe вернёт после оплаты (features/tokenPurchase). */
    returnPath?: string | null;
    /** Язык страницы — сервер вернёт на «Умный поиск» на нём, если путь не подошёл. */
    lang?: string;
}

/**
 * Пакеты токенов больше не продаются этому человеку (410): он на пакетах «поиск + вопросы
 * специалисту» (или выключатель «all»). Страница перечитывает режим и показывает окно
 * пакетов (features/supPlan), а не ошибку оплаты.
 */
export class TokenCheckoutRetiredError extends Error {
    constructor(code = "retired") {
        super(code);
        this.name = "TokenCheckoutRetiredError";
    }
}

/** Сервер не смог узнать режим человека (503 wallet_unavailable): оплату не открываем — повторить позже. */
export class TokenCheckoutUnavailableError extends Error {
    constructor() {
        super("wallet_unavailable");
        this.name = "TokenCheckoutUnavailableError";
    }
}

export async function createQuestionsCheckout(
    email: string,
    packageType: string,
    back: QuestionsCheckoutReturn = {},
): Promise<{ id: string; url: string }> {
    const res = await fetch(AVATAR_PAYMENT_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            customer_email: email,
            package_type: packageType,
            // A friend's referral link (30 days) — shared/lib/referral.ts.
            ...referralBody(),
            // Back to the page it was bought from; the server checks the path.
            ...(back.returnPath ? { return_path: back.returnPath } : {}),
            ...(back.lang ? { lang: back.lang } : {}),
            // GA client/session ids, click ids, utm — the server reports the
            // paid order to GA4 with them (shared/lib/analytics).
            attribution: getAttributionPayload(),
        }),
    });
    if (res.status === 410) {
        const body = (await res.json().catch(() => null)) as { detail?: string } | null;
        throw new TokenCheckoutRetiredError(body?.detail || "retired");
    }
    if (res.status === 503) throw new TokenCheckoutUnavailableError();
    const data = await res.json();
    if (!data.success || !data.data?.url) {
        throw new Error(data.message || "Payment creation error");
    }
    // Here, not at the callers: every token-pack checkout (SUP, specialist chat) passes through.
    trackTokenPackCheckout(packageType);
    return data.data;
}

export async function checkPaymentStatus(
    invId: string,
): Promise<"paid" | "pending" | "error"> {
    try {
        const res = await fetch(
            `${BACKEND_API_URL}/api/payment/check/${invId}`,
        );
        const data = await res.json();
        return data.data?.status || "pending";
    } catch {
        return "error";
    }
}
